import React, { lazy, Suspense } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLocaleContext } from '../context/LocaleContext';
import WatermarkRemoverClient from '../components/WatermarkRemover.client';
import { SocialProof } from '../components/SocialProof';
import { generateFAQSchema, injectFAQSchema } from '../utils/faqSchema';
import { RelatedContentForPSEO } from '../components/RelatedContentForPSEO';

const FeedbackCTA = lazy(() => import('../components/FeedbackCTA'));
const RelatedTools = lazy(() => import('../components/RelatedTools'));
import GeminiImageBanner from '../components/GeminiImageBanner';
import {
  CheckCircle2,
  Shield,
  Zap,
  GraduationCap,
  Briefcase,
  PenTool,
  ChevronDown,
  ArrowRight,
  Sparkles,
  HelpCircle,
  Lock,
  Bot,
  Code,
  FileText,
  Users,
  Mail,
  FlaskConical,
  Crown,
  Star,
  BookOpen,
} from 'lucide-react';

type LocaleKey = 'en' | 'de' | 'pt-BR' | 'ms-MY' | 'es' | 'fr' | 'ja' | 'id';

/**
 * Layout 3: Landing Page Style (Commercial SEO)
 * Visual hierarchy with commercial feel, upgrade CTA emphasis.
 * Hero with badge row, quick benefits, use case grid, upgrade section.
 */
export default function FreeGPTWatermarkRemoverPage() {
  const { user } = useAuth();
  const { locale } = useLocaleContext();
  const [openFAQIndex, setOpenFAQIndex] = React.useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  const t = {
    hero: {
      title: {
        en: 'Free GPT Watermark Remover',
        de: 'Kostenloser GPT-Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'agua GPT Gratis',
        'ms-MY': 'Penyingkir Tanda Air GPT Percuma',
        es: 'Eliminador de Marca de Agua GPT Gratis',
        fr: 'Supprimeur de Filigrane GPT Gratuit',
        ja: '無料GPT透かし除去ツール',
        id: 'Penghapus Watermark GPT Gratis',
      },
      subtitle: {
        en: 'Clean invisible Unicode characters from text pasted out of ChatGPT or any GPT-based tool. 100% free, browser-based, zero data stored.',
        de: 'Entfernen Sie unsichtbare Unicode-Zeichen aus Text, den Sie aus ChatGPT oder einem anderen GPT-basierten Tool kopiert haben. 100% kostenlos, browserbasiert, keine Daten gespeichert.',
        'pt-BR': 'Limpe caracteres Unicode invisiveis de texto colado do ChatGPT ou de qualquer ferramenta baseada em GPT. 100% gratis, baseado no navegador, zero dados armazenados.',
        'ms-MY': 'Bersihkan aksara Unicode tidak kelihatan daripada teks yang ditampal dari ChatGPT atau mana-mana alat berasaskan GPT. 100% percuma, berasaskan pelayar, tiada data disimpan.',
        es: 'Limpia caracteres Unicode invisibles de texto pegado desde ChatGPT o cualquier herramienta basada en GPT. 100% gratis, basado en navegador, cero datos almacenados.',
        fr: 'Nettoyez les caractères Unicode invisibles du texte collé depuis ChatGPT ou tout autre outil basé sur GPT. Gratuit, dans le navigateur, aucune donnée stockée.',
        ja: 'ChatGPTやその他のGPTベースのツールからコピーしたテキストの不可視Unicode文字をクリーニング。完全無料、ブラウザ完結、データ保存なし。',
        id: 'Bersihkan karakter Unicode tak terlihat dari teks yang ditempel dari ChatGPT atau tools berbasis GPT lainnya. 100% gratis, berbasis browser, tanpa penyimpanan data.',
      },
      cta: {
        en: 'Start Cleaning, Free',
        de: 'Jetzt bereinigen, kostenlos',
        'pt-BR': 'Comecar a Limpar, Gratis',
        'ms-MY': 'Mula Bersihkan, Percuma',
        es: 'Empezar a Limpiar, Gratis',
        fr: 'Commencer, Gratuit',
        ja: '今すぐ無料で開始',
        id: 'Mulai Bersihkan, Gratis',
      },
    },
    badges: {
      en: ['No Signup', '500 Chars Free', 'Works With Any GPT Tool', 'Instant Results'],
      de: ['Keine Anmeldung', '500 Zeichen kostenlos', 'Funktioniert mit jedem GPT-Tool', 'Sofortige Ergebnisse'],
      'pt-BR': ['Sem Cadastro', '500 Caracteres Gratis', 'Funciona com Qualquer Ferramenta GPT', 'Resultados Instantaneos'],
      'ms-MY': ['Tanpa Pendaftaran', '500 Aksara Percuma', 'Berfungsi dengan Mana-Mana Alat GPT', 'Keputusan Segera'],
      es: ['Sin Registro', '500 Caracteres Gratis', 'Funciona con Cualquier Herramienta GPT', 'Resultados Instantaneos'],
      fr: ['Sans Inscription', '500 Caractères Gratuits', 'Compatible avec Tout Outil GPT', 'Résultats Immédiats'],
      ja: ['登録不要', '500文字無料', 'あらゆるGPTツールに対応', '即時処理'],
      id: ['Tanpa Daftar', '500 Karakter Gratis', 'Bekerja dengan Tools GPT Apa Pun', 'Hasil Instan'],
    },
    quickBenefits: {
      en: [
        { icon: Zap, text: 'Instant processing in your browser' },
        { icon: Shield, text: 'Your text never leaves your device' },
        { icon: Bot, text: 'Works on characters, so any GPT-based tool is fine' },
      ],
      de: [
        { icon: Zap, text: 'Sofortige Verarbeitung in Ihrem Browser' },
        { icon: Shield, text: 'Ihr Text verlässt nie Ihr Gerät' },
        { icon: Bot, text: 'Arbeitet auf Zeichenebene, also passt jedes GPT-basierte Tool' },
      ],
      'pt-BR': [
        { icon: Zap, text: 'Processamento instantaneo no seu navegador' },
        { icon: Shield, text: 'Seu texto nunca sai do seu dispositivo' },
        { icon: Bot, text: 'Funciona em nivel de caracteres, entao qualquer ferramenta baseada em GPT serve' },
      ],
      'ms-MY': [
        { icon: Zap, text: 'Pemprosesan segera dalam pelayar anda' },
        { icon: Shield, text: 'Teks anda tidak pernah meninggalkan peranti' },
        { icon: Bot, text: 'Berfungsi pada tahap aksara, jadi mana-mana alat berasaskan GPT sesuai' },
      ],
      es: [
        { icon: Zap, text: 'Procesamiento instantaneo en tu navegador' },
        { icon: Shield, text: 'Tu texto nunca sale de tu dispositivo' },
        { icon: Bot, text: 'Funciona a nivel de caracteres, asi que cualquier herramienta basada en GPT sirve' },
      ],
      fr: [
        { icon: Zap, text: 'Traitement instantané dans votre navigateur' },
        { icon: Shield, text: 'Vos textes restent sur votre appareil' },
        { icon: Bot, text: 'Agit au niveau des caractères, donc tout outil basé sur GPT convient' },
      ],
      ja: [
        { icon: Zap, text: 'ブラウザ内で即時処理' },
        { icon: Shield, text: 'テキストはデバイス内で完結' },
        { icon: Bot, text: '文字単位で処理するため、GPTベースのツールなら何でも対応' },
      ],
      id: [
        { icon: Zap, text: 'Pemrosesan instan di browser Anda' },
        { icon: Shield, text: 'Teks Anda tidak pernah keluar dari perangkat' },
        { icon: Bot, text: 'Bekerja di tingkat karakter, jadi tools berbasis GPT apa pun cocok' },
      ],
    },
    whyDifferent: {
      title: {
        en: 'Why Our Free Tool Is Different',
        de: 'Warum unser kostenloses Tool anders ist',
        'pt-BR': 'Por Que Nossa Ferramenta Gratuita e Diferente',
        'ms-MY': 'Mengapa Alat Percuma Kami Berbeza',
        es: 'Por Que Nuestra Herramienta Gratuita Es Diferente',
        fr: 'Pourquoi Notre Outil Gratuit Est Unique',
        ja: '他の無料ツールとの違い',
        id: 'Mengapa Alat Gratis Kami Berbeda',
      },
      items: {
        en: [
          { icon: Lock, title: '100% Privacy', description: 'All processing happens locally. We never see, store, or log your text.' },
          { icon: Shield, title: 'No Data Collection', description: 'No cookies tracking your content. No analytics on what you paste.' },
          { icon: Users, title: 'No Account Required', description: 'Use immediately without email signup or social login.' },
          { icon: Bot, title: 'Model-Independent', description: 'Works on the characters themselves, so the model version behind your text doesn\'t matter.' },
        ],
        de: [
          { icon: Lock, title: '100% Privatsphare', description: 'Alle Verarbeitung erfolgt lokal. Wir sehen, speichern oder protokollieren Ihren Text nie.' },
          { icon: Shield, title: 'Keine Datensammlung', description: 'Keine Cookies, die Ihre Inhalte verfolgen. Keine Analyse dessen, was Sie einfugen.' },
          { icon: Users, title: 'Kein Konto erforderlich', description: 'Sofort nutzen ohne E-Mail-Anmeldung oder Social Login.' },
          { icon: Bot, title: 'Modellunabhängig', description: 'Arbeitet auf Zeichenebene, daher spielt die Modellversion hinter Ihrem Text keine Rolle.' },
        ],
        'pt-BR': [
          { icon: Lock, title: '100% Privacidade', description: 'Todo processamento acontece localmente. Nunca vemos, armazenamos ou registramos seu texto.' },
          { icon: Shield, title: 'Sem Coleta de Dados', description: 'Sem cookies rastreando seu conteudo. Sem analise do que voce cola.' },
          { icon: Users, title: 'Sem Conta Necessaria', description: 'Use imediatamente sem cadastro de email ou login social.' },
          { icon: Bot, title: 'Independente do Modelo', description: 'Funciona em nivel de caracteres, entao a versao do modelo por tras do seu texto nao importa.' },
        ],
        'ms-MY': [
          { icon: Lock, title: '100% Privasi', description: 'Semua pemprosesan berlaku secara tempatan. Kami tidak pernah melihat, menyimpan atau merekod teks anda.' },
          { icon: Shield, title: 'Tiada Pengumpulan Data', description: 'Tiada kuki menjejak kandungan anda. Tiada analitik tentang apa yang anda tampal.' },
          { icon: Users, title: 'Tiada Akaun Diperlukan', description: 'Gunakan serta-merta tanpa pendaftaran email atau log masuk sosial.' },
          { icon: Bot, title: 'Tidak Bergantung Model', description: 'Berfungsi pada tahap aksara, jadi versi model di sebalik teks anda tidak penting.' },
        ],
        es: [
          { icon: Lock, title: '100% Privacidad', description: 'Todo el procesamiento ocurre localmente. Nunca vemos, almacenamos ni registramos tu texto.' },
          { icon: Shield, title: 'Sin Recopilacion de Datos', description: 'Sin cookies rastreando tu contenido. Sin analiticas de lo que pegas.' },
          { icon: Users, title: 'Sin Cuenta Requerida', description: 'Usa inmediatamente sin registro de email o login social.' },
          { icon: Bot, title: 'Independiente del Modelo', description: 'Funciona a nivel de caracteres, asi que la version del modelo detras de tu texto no importa.' },
        ],
        fr: [
          { icon: Lock, title: '100% Confidentialité', description: 'Traitement local uniquement. Nous ne voyons, stockons ni enregistrons jamais vos textes.' },
          { icon: Shield, title: 'Aucune Collecte de Données', description: 'Pas de cookies suivant votre contenu. Aucune analyse de ce que vous collez.' },
          { icon: Users, title: 'Sans Compte Requis', description: 'Utilisez immédiatement sans inscription email ni connexion sociale.' },
          { icon: Bot, title: 'Indépendant du Modèle', description: 'Agit au niveau des caractères, donc la version du modèle derrière votre texte n\'a pas d\'importance.' },
        ],
        ja: [
          { icon: Lock, title: '完全プライバシー保護', description: 'すべての処理はローカルで行われます。テキストの閲覧、保存、記録は一切行いません。' },
          { icon: Shield, title: 'データ収集なし', description: 'コンテンツを追跡するCookieなし。貼り付け内容の分析なし。' },
          { icon: Users, title: 'アカウント不要', description: 'メール登録やソーシャルログインなしで即座に利用可能。' },
          { icon: Bot, title: 'モデルを問わず対応', description: '文字単位で処理するため、背後のモデルバージョンは関係ありません。' },
        ],
        id: [
          { icon: Lock, title: '100% Privasi', description: 'Semua pemrosesan dilakukan secara lokal. Kami tidak pernah melihat, menyimpan, atau mencatat teks Anda.' },
          { icon: Shield, title: 'Tanpa Pengumpulan Data', description: 'Tidak ada cookie yang melacak konten Anda. Tidak ada analitik pada apa yang Anda tempel.' },
          { icon: Users, title: 'Tanpa Akun Diperlukan', description: 'Gunakan langsung tanpa pendaftaran email atau login sosial.' },
          { icon: Bot, title: 'Tidak Bergantung Model', description: 'Bekerja di tingkat karakter, jadi versi model di balik teks Anda tidak masalah.' },
        ],
      },
    },
    useCases: {
      title: {
        en: 'Perfect For Every Use Case',
        de: 'Perfekt fur jeden Anwendungsfall',
        'pt-BR': 'Perfeito Para Cada Caso de Uso',
        'ms-MY': 'Sempurna Untuk Setiap Kes Penggunaan',
        es: 'Perfecto Para Cada Caso de Uso',
        fr: 'Idéal Pour Chaque Utilisation',
        ja: 'あらゆる用途に最適',
        id: 'Sempurna Untuk Setiap Kebutuhan',
      },
      items: {
        en: [
          { icon: GraduationCap, title: 'Students', description: 'Clean GPT-assisted essays and research papers so hidden characters don\'t corrupt citations, break formatting, or skew word and character counts' },
          { icon: Briefcase, title: 'Reports & Proposals', description: 'Remove invisible characters from business documents and presentations' },
          { icon: FileText, title: 'Corporate Documents', description: 'Clean internal memos, emails, and corporate communications' },
          { icon: PenTool, title: 'Essays & Articles', description: 'Prepare polished content where formatting holds and citations survive character for character' },
          { icon: Mail, title: 'Professional Emails', description: 'Send clean, professional emails without hidden characters' },
          { icon: FlaskConical, title: 'Research Papers', description: 'Clean AI-assisted research so citations, footnotes, and character counts stay accurate for academic publication' },
        ],
        de: [
          { icon: GraduationCap, title: 'Studierende', description: 'GPT-assistierte Essays und Forschungsarbeiten bereinigen, damit versteckte Zeichen keine Zitate beschadigen, keine Formatierung storen und keine Wort- und Zeichenzahlungen verfalschen' },
          { icon: Briefcase, title: 'Berichte & Angebote', description: 'Unsichtbare Zeichen aus Geschaftsdokumenten und Prasentationen entfernen' },
          { icon: FileText, title: 'Unternehmensdokumente', description: 'Interne Memos, E-Mails und Unternehmenskommunikation bereinigen' },
          { icon: PenTool, title: 'Essays & Artikel', description: 'Polierten Inhalt vorbereiten, bei dem die Formatierung erhalten bleibt und Zitate zeichengenau erhalten bleiben' },
          { icon: Mail, title: 'Professionelle E-Mails', description: 'Saubere, professionelle E-Mails ohne versteckte Zeichen senden' },
          { icon: FlaskConical, title: 'Forschungsarbeiten', description: 'KI-assistierte Forschung bereinigen, damit Zitate, Fussnoten und Zeichenzahlungen fur die akademische Publikation korrekt bleiben' },
        ],
        'pt-BR': [
          { icon: GraduationCap, title: 'Estudantes', description: 'Limpe ensaios e trabalhos de pesquisa assistidos por GPT para que caracteres ocultos nao corrompam citacoes, quebrem a formatacao ou distorcam a contagem de palavras e caracteres' },
          { icon: Briefcase, title: 'Relatorios & Propostas', description: 'Remova caracteres invisiveis de documentos comerciais e apresentacoes' },
          { icon: FileText, title: 'Documentos Corporativos', description: 'Limpe memorandos internos, emails e comunicacoes corporativas' },
          { icon: PenTool, title: 'Ensaios & Artigos', description: 'Prepare conteudo polido em que a formatacao se mantem e as citacoes sobrevivem caractere por caractere' },
          { icon: Mail, title: 'Emails Profissionais', description: 'Envie emails limpos e profissionais sem caracteres ocultos' },
          { icon: FlaskConical, title: 'Artigos de Pesquisa', description: 'Limpe pesquisa assistida por IA para que citacoes, notas de rodape e contagem de caracteres permanecam precisas para publicacao academica' },
        ],
        'ms-MY': [
          { icon: GraduationCap, title: 'Pelajar', description: 'Bersihkan esei dan kertas penyelidikan yang dibantu GPT supaya aksara tersembunyi tidak merosakkan petikan, mengganggu format, atau mengubah kiraan perkataan dan aksara' },
          { icon: Briefcase, title: 'Laporan & Cadangan', description: 'Buang aksara tidak kelihatan dari dokumen perniagaan dan pembentangan' },
          { icon: FileText, title: 'Dokumen Korporat', description: 'Bersihkan memo dalaman, email dan komunikasi korporat' },
          { icon: PenTool, title: 'Esei & Artikel', description: 'Sediakan kandungan digilap yang formatnya kekal dan petikannya terpelihara aksara demi aksara' },
          { icon: Mail, title: 'Email Profesional', description: 'Hantar email bersih dan profesional tanpa aksara tersembunyi' },
          { icon: FlaskConical, title: 'Kertas Penyelidikan', description: 'Bersihkan penyelidikan dibantu AI supaya petikan, nota kaki dan kiraan aksara kekal tepat untuk penerbitan akademik' },
        ],
        es: [
          { icon: GraduationCap, title: 'Estudiantes', description: 'Limpia ensayos y trabajos de investigacion asistidos por GPT para que los caracteres ocultos no corrompan citas, rompan el formato o distorsionen el recuento de palabras y caracteres' },
          { icon: Briefcase, title: 'Informes & Propuestas', description: 'Elimina caracteres invisibles de documentos comerciales y presentaciones' },
          { icon: FileText, title: 'Documentos Corporativos', description: 'Limpia memorandos internos, emails y comunicaciones corporativas' },
          { icon: PenTool, title: 'Ensayos & Articulos', description: 'Prepara contenido pulido en el que el formato se mantiene y las citas sobreviven caracter a caracter' },
          { icon: Mail, title: 'Emails Profesionales', description: 'Envia emails limpios y profesionales sin caracteres ocultos' },
          { icon: FlaskConical, title: 'Articulos de Investigacion', description: 'Limpia investigacion asistida por IA para que citas, notas al pie y recuento de caracteres se mantengan precisos para publicacion academica' },
        ],
        fr: [
          { icon: GraduationCap, title: 'Étudiants', description: 'Nettoyez vos dissertations et mémoires assistés par GPT afin que les caractères cachés ne corrompent pas vos citations, ne cassent pas la mise en forme et ne faussent pas vos décomptes de mots et de caractères' },
          { icon: Briefcase, title: 'Rapports & Propositions', description: 'Supprimez les caractères invisibles des documents professionnels et présentations' },
          { icon: FileText, title: 'Documents d\'Entreprise', description: 'Nettoyez les notes internes, emails et communications d\'entreprise' },
          { icon: PenTool, title: 'Essais & Articles', description: 'Préparez du contenu soigné où la mise en forme reste intacte et les citations survivent caractère par caractère' },
          { icon: Mail, title: 'Emails Professionnels', description: 'Envoyez des emails propres et professionnels sans caractères cachés' },
          { icon: FlaskConical, title: 'Articles de Recherche', description: 'Nettoyez la recherche assistée par IA pour que citations, notes de bas de page et décompte de caractères restent exacts pour la publication académique' },
        ],
        ja: [
          { icon: GraduationCap, title: '学生向け', description: 'GPT支援のレポートや論文をクリーニングし、隠れた文字が引用を壊したり、書式を崩したり、単語数・文字数を狂わせたりするのを防ぐ' },
          { icon: Briefcase, title: 'レポート・提案書', description: 'ビジネス文書やプレゼン資料から不可視文字を除去' },
          { icon: FileText, title: '企業文書', description: '社内メモ、メール、企業コミュニケーションをクリーン化' },
          { icon: PenTool, title: 'エッセイ・記事', description: '書式が崩れず引用も一字一句正確に保たれる、洗練されたコンテンツを準備' },
          { icon: Mail, title: 'ビジネスメール', description: '隠れた文字のない清潔なビジネスメールを送信' },
          { icon: FlaskConical, title: '研究論文', description: 'AI支援の研究をクリーニングし、引用・脚注・文字数が学術出版に向けて正確なまま保たれるようにする' },
        ],
        id: [
          { icon: GraduationCap, title: 'Mahasiswa', description: 'Bersihkan esai dan makalah penelitian yang dibantu GPT agar karakter tersembunyi tidak merusak kutipan, mengganggu format, atau mengacaukan penghitungan kata dan karakter' },
          { icon: Briefcase, title: 'Laporan & Proposal', description: 'Hapus karakter tak terlihat dari dokumen bisnis dan presentasi' },
          { icon: FileText, title: 'Dokumen Perusahaan', description: 'Bersihkan memo internal, email, dan komunikasi perusahaan' },
          { icon: PenTool, title: 'Esai & Artikel', description: 'Siapkan konten rapi yang formatnya tetap utuh dan kutipannya tetap akurat karakter demi karakter' },
          { icon: Mail, title: 'Email Profesional', description: 'Kirim email bersih dan profesional tanpa karakter tersembunyi' },
          { icon: FlaskConical, title: 'Makalah Penelitian', description: 'Bersihkan penelitian berbantuan AI agar kutipan, catatan kaki, dan penghitungan karakter tetap akurat untuk publikasi akademis' },
        ],
      },
    },
    upgrade: {
      title: {
        en: 'Upgrade for Power Users',
        de: 'Upgrade fur Power-User',
        'pt-BR': 'Upgrade para Usuarios Avancados',
        'ms-MY': 'Naik Taraf untuk Pengguna Kuasa',
        es: 'Mejora para Usuarios Avanzados',
        fr: 'Passez à la Version Pro',
        ja: 'パワーユーザー向けアップグレード',
        id: 'Upgrade untuk Pengguna Profesional',
      },
      subtitle: {
        en: 'Need more? Our premium tier removes all limits.',
        de: 'Brauchen Sie mehr? Unser Premium-Tarif entfernt alle Limits.',
        'pt-BR': 'Precisa de mais? Nosso nivel premium remove todos os limites.',
        'ms-MY': 'Perlukan lebih? Peringkat premium kami menghapuskan semua had.',
        es: 'Necesitas mas? Nuestro nivel premium elimina todos los limites.',
        fr: 'Besoin de plus ? Notre version premium supprime toutes les limites.',
        ja: 'もっと必要ですか？プレミアム版ですべての制限を解除。',
        id: 'Butuh lebih? Paket premium kami menghapus semua batasan.',
      },
      free: {
        title: {
          en: 'Free Tier',
          de: 'Kostenlose Version',
          'pt-BR': 'Versao Gratuita',
          'ms-MY': 'Peringkat Percuma',
          es: 'Version Gratuita',
          fr: 'Version Gratuite',
          ja: '無料版',
          id: 'Versi Gratis',
        },
        features: {
          en: ['500 characters per paste', 'Unlimited daily uses', 'Works with any GPT tool', '100% browser-based'],
          de: ['500 Zeichen pro Einfugen', 'Unbegrenzte tagliche Nutzung', 'Funktioniert mit jedem GPT-Tool', '100% browserbasiert'],
          'pt-BR': ['500 caracteres por colagem', 'Usos diarios ilimitados', 'Funciona com qualquer ferramenta GPT', '100% baseado no navegador'],
          'ms-MY': ['500 aksara setiap tampal', 'Penggunaan harian tanpa had', 'Berfungsi dengan mana-mana alat GPT', '100% berasaskan pelayar'],
          es: ['500 caracteres por pegado', 'Usos diarios ilimitados', 'Funciona con cualquier herramienta GPT', '100% basado en navegador'],
          fr: ['500 caractères par collage', 'Utilisations quotidiennes illimitées', 'Compatible avec tout outil GPT', '100% dans le navigateur'],
          ja: ['1回500文字まで', '毎日無制限に使用可能', 'あらゆるGPTツールに対応', '100%ブラウザベース'],
          id: ['500 karakter per tempel', 'Penggunaan harian tanpa batas', 'Bekerja dengan tools GPT apa pun', '100% berbasis browser'],
        },
      },
      premium: {
        badge: {
          en: 'UPGRADE',
          de: 'UPGRADE',
          'pt-BR': 'UPGRADE',
          'ms-MY': 'NAIK TARAF',
          es: 'MEJORAR',
          fr: 'PASSER PRO',
          ja: 'アップグレード',
          id: 'UPGRADE',
        },
        title: {
          en: 'Premium',
          de: 'Premium',
          'pt-BR': 'Premium',
          'ms-MY': 'Premium',
          es: 'Premium',
          fr: 'Premium',
          ja: 'プレミアム',
          id: 'Premium',
        },
        features: {
          en: ['Unlimited characters', 'Document cleaning (Word, Pages)', 'Better formatting preservation'],
          de: ['Unbegrenzte Zeichen', 'Dokumentenbereinigung (Word, Pages)', 'Bessere Formatierungserhaltung'],
          'pt-BR': ['Caracteres ilimitados', 'Limpeza de documentos (Word, Pages)', 'Melhor preservacao de formatacao'],
          'ms-MY': ['Aksara tanpa had', 'Pembersihan dokumen (Word, Pages)', 'Pemeliharaan format lebih baik'],
          es: ['Caracteres ilimitados', 'Limpieza de documentos (Word, Pages)', 'Mejor preservacion del formato'],
          fr: ['Caractères illimités', 'Nettoyage de documents (Word, Pages)', 'Meilleure préservation du format'],
          ja: ['文字数無制限', 'ドキュメントクリーニング（Word、Pages）', 'より良いフォーマット保持'],
          id: ['Karakter tanpa batas', 'Pembersihan dokumen (Word, Pages)', 'Preservasi format lebih baik'],
        },
      },
      cta: {
        en: 'View Premium Plans',
        de: 'Premium-Plane ansehen',
        'pt-BR': 'Ver Planos Premium',
        'ms-MY': 'Lihat Pelan Premium',
        es: 'Ver Planes Premium',
        fr: 'Voir les Offres Premium',
        ja: 'プレミアムプランを見る',
        id: 'Lihat Paket Premium',
      },
    },
    faqTitle: {
      en: 'Quick Answers',
      de: 'Schnelle Antworten',
      'pt-BR': 'Respostas Rapidas',
      'ms-MY': 'Jawapan Pantas',
      es: 'Respuestas Rapidas',
      fr: 'Réponses Rapides',
      ja: 'よくある質問',
      id: 'Jawaban Cepat',
    },
    relatedContent: {
      title: {
        en: 'Learn More About GPT Watermarks',
        de: 'Erfahren Sie mehr uber GPT-Wasserzeichen',
        'pt-BR': 'Saiba Mais Sobre Marcas d\'agua GPT',
        'ms-MY': 'Ketahui Lebih Lanjut Tentang Tanda Air GPT',
        es: 'Aprende Mas Sobre las Marcas de Agua GPT',
        fr: 'En Savoir Plus sur les Filigranes GPT',
        ja: 'GPT透かしについて詳しく',
        id: 'Pelajari Lebih Lanjut Tentang Watermark GPT',
      },
    },
  };

  const faqs = {
    en: [
      {
        question: 'Is this GPT watermark remover really 100% free?',
        answer: 'Yes! Clean up to 500 characters per paste completely free, forever. No signup, no credit card, no hidden fees. Use unlimited times per day.',
        icon: Sparkles,
      },
      {
        question: 'Does it work with all GPT models?',
        answer: 'Yes, it works with text from any GPT model or AI tool because it scans for specific Unicode characters, not model signatures, so the model version behind your text is irrelevant. To be clear, OpenAI has not shipped text watermarking; what this tool removes are invisible Unicode artefacts, not a company-issued marker. It also has no effect on AI-writing detectors like Turnitin or GPTZero, which analyse your visible writing, not hidden Unicode.',
        icon: Bot,
      },
      {
        question: 'Is my GPT-generated text private?',
        answer: 'Completely. All processing happens locally in your browser using JavaScript. Your text never leaves your device and is never sent to any server.',
        icon: Shield,
      },
      {
        question: 'Will removing watermarks break my text or code?',
        answer: 'No. We only remove invisible Unicode characters (zero-width spaces, joiners, etc.) that have no functional purpose. Your visible content stays 100% intact.',
        icon: Code,
      },
      {
        question: 'Does removing these characters affect AI-writing detection?',
        answer: 'Detectors like Turnitin and GPTZero analyse your visible writing, not invisible Unicode characters, so cleaning has no effect on an AI-detection result either way. This tool removes technical artefacts from your text; it does not change the underlying content or your responsibility to cite AI-assisted work honestly and according to your institution\'s policies.',
        icon: BookOpen,
      },
      {
        question: 'What\'s the difference between free and premium?',
        answer: 'Free: 500 chars per paste, unlimited uses. Premium: Unlimited characters, document cleaning (Word/Pages), and better formatting preservation.',
        icon: Crown,
      },
    ],
    de: [
      {
        question: 'Ist dieser GPT-Wasserzeichen-Entferner wirklich 100% kostenlos?',
        answer: 'Ja! Bereinigen Sie bis zu 500 Zeichen pro Einfugen vollig kostenlos, fur immer. Keine Anmeldung, keine Kreditkarte, keine versteckten Gebuhren.',
        icon: Sparkles,
      },
      {
        question: 'Funktioniert es mit allen GPT-Modellen?',
        answer: 'Ja, es funktioniert mit Text aus jedem GPT-Modell oder KI-Tool, da es nach bestimmten Unicode-Zeichen sucht, nicht nach Modellsignaturen; die Modellversion hinter Ihrem Text spielt also keine Rolle. Zur Klarstellung: OpenAI hat keine Text-Wasserzeichen eingefuhrt; was dieses Tool entfernt, sind unsichtbare Unicode-Artefakte, kein von OpenAI vergebenes Merkmal. Es hat auch keinen Einfluss auf KI-Schreibdetektoren wie Turnitin oder GPTZero, die Ihren sichtbaren Text analysieren, nicht verstecktes Unicode.',
        icon: Bot,
      },
      {
        question: 'Ist mein GPT-generierter Text privat?',
        answer: 'Vollstandig. Alle Verarbeitung erfolgt lokal in Ihrem Browser. Ihr Text verlasst nie Ihr Gerat und wird nie an einen Server gesendet.',
        icon: Shield,
      },
      {
        question: 'Wird das Entfernen von Wasserzeichen meinen Text beschadigen?',
        answer: 'Nein. Wir entfernen nur unsichtbare Unicode-Zeichen ohne funktionalen Zweck. Ihr sichtbarer Inhalt bleibt 100% intakt.',
        icon: Code,
      },
      {
        question: 'Beeinflusst das Entfernen dieser Zeichen die KI-Texterkennung?',
        answer: 'Detektoren wie Turnitin und GPTZero analysieren Ihren sichtbaren Text, nicht unsichtbare Unicode-Zeichen; das Bereinigen hat daher keinen Einfluss auf ein KI-Erkennungsergebnis. Dieses Tool entfernt technische Artefakte aus Ihrem Text; es andert nicht den zugrunde liegenden Inhalt oder Ihre Pflicht, KI-unterstutzte Arbeit ehrlich und gemass den Richtlinien Ihrer Institution zu zitieren.',
        icon: BookOpen,
      },
      {
        question: 'Was ist der Unterschied zwischen kostenlos und Premium?',
        answer: 'Kostenlos: 500 Zeichen pro Einfugen, unbegrenzte Nutzung. Premium: Unbegrenzte Zeichen, Dokumentenbereinigung, bessere Formatierungserhaltung.',
        icon: Crown,
      },
    ],
    'pt-BR': [
      {
        question: 'Este removedor de marca d\'agua GPT e realmente 100% gratis?',
        answer: 'Sim! Limpe ate 500 caracteres por colagem completamente gratis, para sempre. Sem cadastro, sem cartao de credito, sem taxas ocultas.',
        icon: Sparkles,
      },
      {
        question: 'Funciona com todos os modelos GPT?',
        answer: 'Sim, funciona com texto de qualquer modelo GPT ou ferramenta de IA porque escaneia caracteres Unicode especificos, nao assinaturas de modelo, entao a versao do modelo por tras do seu texto e irrelevante. Para deixar claro: a OpenAI nao lancou marca d\'agua de texto; o que esta ferramenta remove sao artefatos Unicode invisiveis, nao um marcador emitido pela empresa. Tambem nao afeta detectores de escrita por IA como Turnitin ou GPTZero, que analisam seu texto visivel, nao Unicode oculto.',
        icon: Bot,
      },
      {
        question: 'Meu texto gerado por GPT e privado?',
        answer: 'Completamente. Todo processamento acontece localmente no seu navegador. Seu texto nunca sai do seu dispositivo.',
        icon: Shield,
      },
      {
        question: 'Remover marcas d\'agua vai quebrar meu texto?',
        answer: 'Nao. Removemos apenas caracteres Unicode invisiveis sem proposito funcional. Seu conteudo visivel permanece 100% intacto.',
        icon: Code,
      },
      {
        question: 'Remover esses caracteres afeta a deteccao de escrita por IA?',
        answer: 'Detectores como Turnitin e GPTZero analisam seu texto visivel, nao caracteres Unicode invisiveis, entao a limpeza nao tem efeito sobre um resultado de deteccao de IA. Esta ferramenta remove artefatos tecnicos do seu texto; nao altera o conteudo subjacente nem sua responsabilidade de citar trabalho assistido por IA com honestidade e conforme as politicas da sua instituicao.',
        icon: BookOpen,
      },
      {
        question: 'Qual a diferenca entre gratis e premium?',
        answer: 'Gratis: 500 caracteres por colagem, usos ilimitados. Premium: Caracteres ilimitados, limpeza de documentos, melhor preservacao de formatacao.',
        icon: Crown,
      },
    ],
    'ms-MY': [
      {
        question: 'Adakah penyingkir tanda air GPT ini benar-benar 100% percuma?',
        answer: 'Ya! Bersihkan sehingga 500 aksara setiap tampal sepenuhnya percuma, selama-lamanya. Tiada pendaftaran, tiada kad kredit, tiada yuran tersembunyi.',
        icon: Sparkles,
      },
      {
        question: 'Adakah ia berfungsi dengan semua model GPT?',
        answer: 'Ya, ia berfungsi dengan teks dari mana-mana model GPT atau alat AI kerana ia mengimbas aksara Unicode tertentu, bukan tandatangan model, jadi versi model di sebalik teks anda tidak penting. Untuk jelas: OpenAI tidak melancarkan tanda air teks; apa yang alat ini buang adalah artifak Unicode tidak kelihatan, bukan penanda yang dikeluarkan syarikat. Ia juga tidak menjejaskan pengesan penulisan AI seperti Turnitin atau GPTZero, yang menganalisis teks anda yang kelihatan, bukan Unicode tersembunyi.',
        icon: Bot,
      },
      {
        question: 'Adakah teks GPT saya peribadi?',
        answer: 'Sepenuhnya. Semua pemprosesan berlaku secara tempatan dalam pelayar anda. Teks anda tidak pernah meninggalkan peranti anda.',
        icon: Shield,
      },
      {
        question: 'Adakah membuang tanda air akan merosakkan teks saya?',
        answer: 'Tidak. Kami hanya membuang aksara Unicode tidak kelihatan tanpa tujuan fungsi. Kandungan anda kekal 100% utuh.',
        icon: Code,
      },
      {
        question: 'Adakah membuang aksara ini menjejaskan pengesanan penulisan AI?',
        answer: 'Pengesan seperti Turnitin dan GPTZero menganalisis teks anda yang kelihatan, bukan aksara Unicode tidak kelihatan, jadi pembersihan tidak memberi kesan pada keputusan pengesanan AI. Alat ini membuang artifak teknikal daripada teks anda; ia tidak mengubah kandungan asas atau tanggungjawab anda untuk memetik kerja yang dibantu AI secara jujur dan mengikut polisi institusi anda.',
        icon: BookOpen,
      },
      {
        question: 'Apa perbezaan antara percuma dan premium?',
        answer: 'Percuma: 500 aksara setiap tampal, penggunaan tanpa had. Premium: Aksara tanpa had, pembersihan dokumen, pemeliharaan format lebih baik.',
        icon: Crown,
      },
    ],
    es: [
      {
        question: 'Es este eliminador de marcas de agua GPT realmente 100% gratis?',
        answer: 'Si! Limpia hasta 500 caracteres por pegado completamente gratis, para siempre. Sin registro, sin tarjeta de credito, sin tarifas ocultas.',
        icon: Sparkles,
      },
      {
        question: 'Funciona con todos los modelos GPT?',
        answer: 'Si, funciona con texto de cualquier modelo GPT o herramienta de IA porque escanea caracteres Unicode especificos, no firmas de modelo, asi que la version del modelo detras de tu texto es irrelevante. Para que quede claro: OpenAI no ha lanzado marcas de agua de texto; lo que esta herramienta elimina son artefactos Unicode invisibles, no un marcador emitido por la empresa. Tampoco afecta a detectores de escritura por IA como Turnitin o GPTZero, que analizan tu texto visible, no el Unicode oculto.',
        icon: Bot,
      },
      {
        question: 'Es privado mi texto generado por GPT?',
        answer: 'Completamente. Todo el procesamiento ocurre localmente en tu navegador. Tu texto nunca sale de tu dispositivo.',
        icon: Shield,
      },
      {
        question: 'Eliminar marcas de agua rompera mi texto?',
        answer: 'No. Solo eliminamos caracteres Unicode invisibles sin proposito funcional. Tu contenido visible permanece 100% intacto.',
        icon: Code,
      },
      {
        question: 'Eliminar estos caracteres afecta la deteccion de escritura por IA?',
        answer: 'Detectores como Turnitin y GPTZero analizan tu texto visible, no caracteres Unicode invisibles, asi que limpiarlos no tiene efecto sobre un resultado de deteccion de IA. Esta herramienta elimina artefactos tecnicos de tu texto; no cambia el contenido subyacente ni tu responsabilidad de citar el trabajo asistido por IA con honestidad y segun las politicas de tu institucion.',
        icon: BookOpen,
      },
      {
        question: 'Cual es la diferencia entre gratis y premium?',
        answer: 'Gratis: 500 caracteres por pegado, usos ilimitados. Premium: Caracteres ilimitados, limpieza de documentos, mejor preservacion del formato.',
        icon: Crown,
      },
    ],
    fr: [
      {
        question: 'Ce supprimeur de filigrane GPT est-il vraiment 100% gratuit ?',
        answer: 'Oui ! Nettoyez jusqu\'à 500 caractères par collage gratuitement, pour toujours. Sans inscription, sans carte bancaire, sans frais cachés.',
        icon: Sparkles,
      },
      {
        question: 'Fonctionne-t-il avec tous les modèles GPT ?',
        answer: 'Oui, il fonctionne avec du texte de tout modèle GPT ou outil IA car il recherche des caractères Unicode spécifiques, pas des signatures de modèle, donc la version du modèle derrière votre texte n\'a pas d\'importance. Pour être clair : OpenAI n\'a pas déployé de filigrane de texte ; ce que cet outil supprime, ce sont des artefacts Unicode invisibles, pas un marqueur émis par l\'entreprise. Il n\'a pas non plus d\'effet sur les détecteurs d\'écriture IA comme Turnitin ou GPTZero, qui analysent votre texte visible, et non l\'Unicode caché.',
        icon: Bot,
      },
      {
        question: 'Mon texte généré par GPT reste-t-il confidentiel ?',
        answer: 'Totalement. Tout le traitement se fait localement dans votre navigateur. Votre texte ne quitte jamais votre appareil.',
        icon: Shield,
      },
      {
        question: 'La suppression des filigranes endommagera-t-elle mon texte ?',
        answer: 'Non. Nous supprimons uniquement les caractères Unicode invisibles sans fonction utile. Votre contenu visible reste 100% intact.',
        icon: Code,
      },
      {
        question: 'Supprimer ces caractères affecte-t-il la détection d\'écriture IA ?',
        answer: 'Les détecteurs comme Turnitin et GPTZero analysent votre texte visible, pas les caractères Unicode invisibles ; le nettoyage n\'a donc aucun effet sur un résultat de détection IA. Cet outil supprime des artefacts techniques de votre texte ; il ne modifie pas le contenu sous-jacent ni votre responsabilité de citer honnêtement le travail assisté par IA selon les politiques de votre établissement.',
        icon: BookOpen,
      },
      {
        question: 'Quelle est la différence entre gratuit et premium ?',
        answer: 'Gratuit : 500 caractères par collage, utilisations illimitées. Premium : Caractères illimités, nettoyage de documents, meilleure préservation du format.',
        icon: Crown,
      },
    ],
    ja: [
      {
        question: 'このGPT透かし除去ツールは本当に100%無料ですか？',
        answer: 'はい！1回500文字まで完全無料で永久に利用可能。登録不要、クレジットカード不要、隠れた料金もありません。',
        icon: Sparkles,
      },
      {
        question: 'すべてのGPTモデルに対応していますか？',
        answer: 'はい、モデルの署名ではなく特定のUnicode文字をスキャンするため、どのGPTモデルやAIツールのテキストにも対応しており、背後のモデルバージョンは関係ありません。念のため明確にしておくと、OpenAIはテキスト透かしを導入していません。このツールが除去するのは不可視のUnicodeアーティファクトであり、企業が発行したマーカーではありません。またTurnitinやGPTZeroのようなAI文章検出ツールにも影響しません。これらのツールは隠れたUnicodeではなく、表示される文章を分析します。',
        icon: Bot,
      },
      {
        question: 'GPTで生成したテキストのプライバシーは守られますか？',
        answer: '完全に守られます。すべての処理はブラウザ内でローカルに行われます。テキストがデバイスから出ることは決してありません。',
        icon: Shield,
      },
      {
        question: '透かしを除去するとテキストが壊れませんか？',
        answer: 'いいえ。ゼロ幅スペースなど機能的な目的のない不可視のUnicode文字のみを除去します。表示されるコンテンツは100%そのままです。',
        icon: Code,
      },
      {
        question: 'これらの文字を除去するとAI文章検出に影響しますか？',
        answer: 'TurnitinやGPTZeroのような検出ツールは、不可視のUnicode文字ではなく、表示される文章を分析するため、クリーニングはAI検出結果に一切影響しません。当ツールはテキストから技術的なアーティファクトを除去するだけであり、内容そのものや、所属機関のポリシーに従ってAI支援作業を誠実に引用する責任を変えるものではありません。',
        icon: BookOpen,
      },
      {
        question: '無料版とプレミアム版の違いは何ですか？',
        answer: '無料版：1回500文字、無制限に使用可能。プレミアム版：文字数無制限、ドキュメントクリーニング、より良いフォーマット保持。',
        icon: Crown,
      },
    ],
    id: [
      {
        question: 'Apakah penghapus watermark GPT ini benar-benar 100% gratis?',
        answer: 'Ya! Bersihkan hingga 500 karakter per tempel secara gratis selamanya. Tanpa pendaftaran, tanpa kartu kredit, tanpa biaya tersembunyi.',
        icon: Sparkles,
      },
      {
        question: 'Apakah ini bekerja dengan semua model GPT?',
        answer: 'Ya, ini bekerja dengan teks dari model GPT atau tools AI mana pun karena memindai karakter Unicode tertentu, bukan tanda tangan model, jadi versi model di balik teks Anda tidak relevan. Agar jelas, OpenAI belum meluncurkan watermark teks; yang dihapus alat ini adalah artefak Unicode tak terlihat, bukan penanda resmi dari perusahaan. Alat ini juga tidak berpengaruh pada detektor tulisan AI seperti Turnitin atau GPTZero, yang menganalisis teks Anda yang terlihat, bukan Unicode tersembunyi.',
        icon: Bot,
      },
      {
        question: 'Apakah teks GPT saya tetap privat?',
        answer: 'Sepenuhnya. Semua pemrosesan dilakukan secara lokal di browser Anda. Teks Anda tidak pernah keluar dari perangkat Anda.',
        icon: Shield,
      },
      {
        question: 'Apakah menghapus watermark akan merusak teks saya?',
        answer: 'Tidak. Kami hanya menghapus karakter Unicode tak terlihat tanpa tujuan fungsional. Konten yang terlihat tetap 100% utuh.',
        icon: Code,
      },
      {
        question: 'Apakah menghapus karakter ini memengaruhi deteksi tulisan AI?',
        answer: 'Detektor seperti Turnitin dan GPTZero menganalisis teks Anda yang terlihat, bukan karakter Unicode tak terlihat, jadi pembersihan tidak berpengaruh pada hasil deteksi AI. Alat ini menghapus artefak teknis dari teks Anda; ini tidak mengubah konten yang mendasarinya atau tanggung jawab Anda untuk mengutip pekerjaan yang dibantu AI secara jujur dan sesuai kebijakan institusi Anda.',
        icon: BookOpen,
      },
      {
        question: 'Apa perbedaan antara gratis dan premium?',
        answer: 'Gratis: 500 karakter per tempel, penggunaan tanpa batas. Premium: Karakter tanpa batas, pembersihan dokumen, preservasi format lebih baik.',
        icon: Crown,
      },
    ],
  };

  const currentFAQs = faqs[locale as LocaleKey] || faqs.en;
  const currentLocale = locale as LocaleKey;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const faqSchema = generateFAQSchema(currentFAQs);
      const cleanup = injectFAQSchema(faqSchema);
      return cleanup;
    } catch (error) {
      console.error('[FreeGPT FAQ] Failed to generate or inject FAQ schema:', error);
    }
  }, [locale, currentFAQs]);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Visual Badge Row */}
      {/* CLS FIX: min-height reserves space before hydration */}
      <section className="relative overflow-hidden py-12 px-4 min-h-[480px] md:min-h-[420px] lg:min-h-[400px]">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 blur-3xl opacity-20 w-[30rem] h-[30rem]">
            <div className="h-full w-full rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-red-400"></div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400">
              {t.hero.title[currentLocale]}
            </span>
          </h1>

          <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 mb-6 max-w-2xl mx-auto">
            {t.hero.subtitle[currentLocale]}
          </p>

          {/* Badge Row */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {t.badges[currentLocale].map((badge, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-charcoal-800 text-neutral-700 dark:text-neutral-300 rounded-full text-sm font-medium border border-neutral-200 dark:border-charcoal-700 shadow-sm"
              >
                <CheckCircle2 size={14} className="text-green-500" />
                {badge}
              </span>
            ))}
          </div>

          {/* Social Proof */}
          <div className="flex justify-center mb-6">
            <SocialProof className="max-w-sm" />
          </div>

          <a
            href="#remover"
            className="group inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-xl font-semibold professional-transition shadow-lg hover:shadow-xl active:scale-[0.98]"
          >
            {t.hero.cta[currentLocale]}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* Tool Section */}
      <section id="remover" className="py-8">
        <div className="max-w-[1400px] mx-auto px-4">
          <WatermarkRemoverClient user={user} />
        </div>

        {/* Quick Benefits Below Tool */}
        <div className="max-w-[1400px] mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-6">
            {t.quickBenefits[currentLocale].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                  <Icon size={18} className="text-amber-600 dark:text-amber-400" />
                  <span className="text-sm">{benefit.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Our Tool Is Different Section */}
      <section className="py-12 max-w-[1400px] mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-8">
          {t.whyDifferent.title[currentLocale]}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.whyDifferent.items[currentLocale].map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="bg-white dark:bg-charcoal-800 p-5 rounded-xl border border-neutral-200 dark:border-charcoal-700 text-center"
              >
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon size={20} className="text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Use Cases Grid (6 Cards) */}
      <section className="py-12 bg-gradient-to-b from-transparent via-amber-50/30 to-transparent dark:via-amber-900/5">
        <div className="max-w-[1400px] mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-8">
            {t.useCases.title[currentLocale]}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.useCases.items[currentLocale].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-charcoal-800 p-5 rounded-xl border border-neutral-200 dark:border-charcoal-700 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Upgrade for Power Users CTA Section */}
      <section className="py-12 max-w-[1400px] mx-auto px-4">
        <div className="bg-gradient-to-br from-amber-600 to-orange-600 rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              {t.upgrade.title[currentLocale]}
            </h2>
            <p className="text-amber-100">
              {t.upgrade.subtitle[currentLocale]}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Tier */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Star size={20} />
                {t.upgrade.free.title[currentLocale]}
              </h3>
              <ul className="space-y-2">
                {t.upgrade.free.features[currentLocale].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-amber-100">
                    <CheckCircle2 size={16} className="text-white flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium Tier */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 relative">
              <div className="absolute -top-3 right-4 px-3 py-1 bg-white text-amber-600 text-xs font-bold rounded-full">
                {t.upgrade.premium.badge[currentLocale]}
              </div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Crown size={20} />
                {t.upgrade.premium.title[currentLocale]}
              </h3>
              <ul className="space-y-2">
                {t.upgrade.premium.features[currentLocale].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 size={16} className="text-white flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-8">
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 bg-white text-amber-600 hover:bg-amber-50 px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              {t.upgrade.cta[currentLocale]}
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Related Content */}
      <div className="py-8 max-w-[1400px] mx-auto px-4">
        <RelatedContentForPSEO
          pageId="free-gpt-watermark"
          pageTitle={t.relatedContent.title[currentLocale]}
          pageDescription={
            locale === 'de'
              ? 'Entfernen Sie unsichtbare GPT-Wasserzeichen'
              : locale === 'pt-BR'
              ? 'Remova marcas d\'agua GPT invisiveis'
              : locale === 'ms-MY'
              ? 'Buang tanda air GPT tidak kelihatan'
              : locale === 'es'
              ? 'Elimina marcas de agua GPT invisibles'
              : locale === 'fr'
              ? 'Supprimez les filigranes GPT invisibles'
              : locale === 'ja'
              ? '見えないGPT透かしを削除'
              : locale === 'id'
              ? 'Hapus watermark GPT yang tidak terlihat'
              : 'Remove invisible GPT watermarks'
          }
          locale={locale}
        />
      </div>

      {/* Mini FAQ Section (5 Q&A) */}
      <section className="py-12 max-w-[1400px] mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-neutral-900 dark:text-white text-center flex items-center justify-center gap-2">
          <HelpCircle size={28} className="text-amber-600 dark:text-amber-400" />
          {t.faqTitle[currentLocale]}
        </h2>

        <div className="space-y-2">
          {currentFAQs.map((faq, index) => {
            const Icon = faq.icon;
            const isOpen = openFAQIndex === index;

            return (
              <div
                key={index}
                className="border border-neutral-200 dark:border-charcoal-700 rounded-lg overflow-hidden bg-white dark:bg-charcoal-800"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex-shrink-0">
                      <Icon size={18} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {faq.question}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 pb-4' : 'max-h-0'}`}>
                  <div className="px-4 pl-14">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Internal linking banner - Gemini Image Watermark Remover */}
      <GeminiImageBanner />

      <Suspense fallback={null}>
        <RelatedTools currentPage="free-gpt" />
      </Suspense>

      <Suspense fallback={null}>
        <FeedbackCTA />
      </Suspense>
    </div>
  );
}
